<template>
  <div class="planning-revenue-drawer" :class="{ 'planning-revenue-drawer--open': show }">
    <div class="planning-revenue-drawer__overlay" @click="$emit('close')"></div>
    <div class="planning-revenue-drawer__content">
      <div class="planning-revenue-drawer__header">
        <div>
          <h2 class="planning-revenue-drawer__title">Geplande omzet</h2>
          <p class="planning-revenue-drawer__period">{{ periodLabel }}</p>
        </div>
        <button
          type="button"
          class="planning-revenue-drawer__close"
          title="Sluiten"
          @click="$emit('close')"
        >
          <BaseIcon name="xmark" size="20" />
        </button>
      </div>

      <div class="planning-revenue-drawer__totals">
        <div class="revenue-total">
          <span class="revenue-total__label">Bevestigd</span>
          <span class="revenue-total__value">{{ formatCurrency(summary.planned_confirmed) }}</span>
        </div>
        <div class="revenue-total">
          <span class="revenue-total__label">Voorlopig</span>
          <span class="revenue-total__value">{{ formatCurrency(summary.planned_tentative) }}</span>
        </div>
        <div class="revenue-total">
          <span class="revenue-total__label">Nog niet gehangen</span>
          <span class="revenue-total__value">{{ formatCurrency(summary.unplanned) }}</span>
        </div>
        <div class="revenue-total">
          <span class="revenue-total__label">Afgerond</span>
          <span class="revenue-total__value">{{ formatCurrency(summary.realized) }}</span>
        </div>
      </div>

      <div class="planning-revenue-drawer__body">
        <div v-for="section in sections" :key="section.key" class="drawer-section">
          <h3 class="drawer-section__title">
            {{ section.title }}
            <span class="drawer-section__meta">
              {{ formatCurrency(section.total) }} · {{ section.items.length }}
            </span>
          </h3>
          <button
            v-for="item in section.items"
            :key="`${section.key}-${item.work_order_id}`"
            type="button"
            class="revenue-row"
            @click="$emit('select', item)"
          >
            <div class="revenue-row__main">
              <span class="revenue-row__customer">
                {{ item.customer_name || item.work_order_number || 'Onbekende klant' }}
              </span>
              <span class="revenue-row__meta">
                {{ item.work_order_number }}
                <template v-if="item.project_number"> · {{ item.project_number }}</template>
              </span>
            </div>
            <span class="revenue-row__value">{{ formatCurrency(item.value_excl_vat) }}</span>
          </button>
          <p v-if="section.items.length === 0" class="drawer-section__empty">Geen items</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { formatCurrency } from '@/utils/statsFormat';

  const SECTION_DEFS = [
    { key: 'confirmed', title: 'Bevestigd in deze periode', bucket: 'confirmed' },
    { key: 'tentative', title: 'Voorlopig in deze periode', bucket: 'tentative' },
    { key: 'unplanned', title: 'Nog niet gehangen', bucket: 'unplanned' },
    { key: 'realized', title: 'Afgerond in deze periode', bucket: 'realized' },
  ];

  export default {
    name: 'PlanningRevenueDrawer',
    components: { BaseIcon },
    props: {
      show: { type: Boolean, default: false },
      periodLabel: { type: String, default: '' },
      summary: { type: Object, default: () => ({}) },
      items: { type: Array, default: () => [] },
    },
    emits: ['close', 'select'],
    setup(props) {
      const sections = computed(() =>
        SECTION_DEFS.map((def) => {
          const sectionItems = (props.items || []).filter((item) =>
            (item.buckets || []).includes(def.bucket)
          );
          const total = sectionItems.reduce(
            (sum, item) => sum + (Number(item.value_excl_vat) || 0),
            0
          );
          return { ...def, items: sectionItems, total };
        })
      );

      return { sections, formatCurrency };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .planning-revenue-drawer {
    pointer-events: none;

    &--open {
      pointer-events: auto;
    }

    &__overlay {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.35);
      opacity: 0;
      transition: opacity 0.2s ease;
      z-index: 40;
    }

    &--open &__overlay {
      opacity: 1;
    }

    &__content {
      position: fixed;
      top: 0;
      right: 0;
      width: min(28rem, 100vw);
      height: 100vh;
      background: white;
      box-shadow: -8px 0 24px rgba(15, 23, 42, 0.12);
      transform: translateX(100%);
      transition: transform 0.2s ease;
      z-index: 41;
      display: flex;
      flex-direction: column;
    }

    &--open &__content {
      transform: translateX(0);
    }

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      padding: 1.25rem 1.25rem 1rem;
      border-bottom: 1px solid var(--slate-200);
    }

    &__title {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__period {
      margin: 0.25rem 0 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border: none;
      background: transparent;
      color: var(--slate-500);
      cursor: pointer;
      border-radius: 0.375rem;

      @include hover {
        background: var(--slate-100);
        color: var(--slate-800);
      }
    }

    &__totals {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid var(--slate-100);
    }

    &__body {
      flex: 1;
      overflow-y: auto;
      padding: 0.75rem 1.25rem 1.5rem;
    }
  }

  .revenue-total {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.625rem 0.75rem;
    background: var(--slate-50);
    border-radius: 0.5rem;

    &__label {
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    &__value {
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
    }
  }

  .drawer-section {
    margin-top: 1rem;

    &__title {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
      margin: 0 0 0.5rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-600);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    &__meta {
      font-weight: 500;
      text-transform: none;
      letter-spacing: 0;
      color: var(--slate-500);
    }

    &__empty {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-400);
    }
  }

  .revenue-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    padding: 0.625rem 0.75rem;
    margin-bottom: 0.35rem;
    border: 1px solid var(--slate-150, var(--slate-200));
    background: white;
    border-radius: 0.5rem;
    text-align: left;
    cursor: pointer;

    @include hover {
      border-color: var(--brand-300);
      background: var(--brand-50);
    }

    &__main {
      min-width: 0;
    }

    &__customer {
      display: block;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-800);
      @include ellipsis(1);
    }

    &__meta {
      display: block;
      margin-top: 0.1rem;
      font-size: 0.6875rem;
      color: var(--slate-500);
    }

    &__value {
      flex-shrink: 0;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-900);
    }
  }
</style>
